import { useState, useCallback, useRef, useEffect } from 'react'
// Badge rendering is PNG-only (Issue #55 — no SVG badges). The Verifieddit
// PNG at https://verifieddit.com/api/badges/{shortId}.png is the durable
// artifact and only exists post-confirmed-upload. The local SVG generator
// is no longer imported anywhere in the upload flow.
import { computeFileHash } from './utils/computeFileHash'
import { generateThumbnail } from './utils/generateThumbnail'
import { createVerification, checkExistingHash } from './utils/verifiedditApi'
import { extractC2paLocally } from './utils/c2paClient'
import { ProvenanceDiagram } from './components/ProvenanceDiagram'
import { generateMermaidDiagram } from './utils/mermaidGenerator'
import { verifyUrlViaHub, hubResponseToServerVerification } from './utils/hubVerifyApi'
import { exportVerificationCertificate } from './utils/exportCertificate'
import { isEcosystemSigner } from './utils/credentialRecovery'
import { AiDetectionBanner } from './components/AiDetectionBanner'
import { GPSMap } from './components/GPSMap'
import { analyzeImageForAi, hasAiDetectConsent } from './utils/aiDetectionApi'
import type { AiDetectApiResponse } from './utils/aiDetectionApi'
import type { BadgeStatus } from './utils/generateBadge'

/** Derive the Verifieddit badge PNG URL from a ddit.wtf short URL.
    Returns null if shortUrl is empty or malformed (no shortId). */
function badgePngUrlFromShortUrl(shortUrl: string | null | undefined): string | null {
  if (!shortUrl || !shortUrl.includes('/')) return null
  const shortId = shortUrl.replace(/\/$/, '').split('/').pop()
  return shortId ? `https://verifieddit.com/api/badges/${shortId}.png` : null
}

// When served from www.thephenom.app (CF Pages), API calls go to drop.thephenom.app (CORS enabled)
// API host resolution. drop.thephenom.app and any www host that proxies
// /api/drop/* (production www + preview deployments via the catchall Pages
// Function) are same-origin to the SPA — use relative paths. Any other
// host (NAS, localhost, raw pages.dev probes) talks to drop directly.
const API_BASE = (() => {
  if (typeof window === 'undefined') return ''
  const h = window.location.hostname
  if (h === 'drop.thephenom.app') return ''
  if (h === 'www.thephenom.app' || h === 'thephenom.app') return ''
  if (h.endsWith('.pages.dev')) return ''
  return 'https://drop.thephenom.app'
})()

const UPLOAD_API = import.meta.env.VITE_UPLOAD_API_ENDPOINT || `${API_BASE}/api/drop/upload`

const MAX_FILE_SIZE = 2_000_000_000 // 2 GB

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return (bytes / Math.pow(k, i)).toFixed(i >= 2 ? 1 : 0) + ' ' + sizes[i]
}

type VerificationState = 'idle' | 'verifying' | 'verified' | 'uploading' | 'uploaded' | 'error' | 'rejected'

interface VerificationResult {
  hasC2PA: boolean
  isValid: boolean
  isTrusted: boolean
  hasTimestamp: boolean
  issuer?: string
  signedAt?: string
  claimGenerator?: string
  badgeStatus: BadgeStatus
  aiDetection?: { isAiGenerated: boolean; note: string; detectedTools: string[]; digitalSourceType?: string }
  formatNote?: string
  pendingServerVerification?: boolean
  gpsCoords?: { lat: number; lon: number }
  /** Raw C2PA manifest store, read locally — drives the provenance diagram. */
  manifestStore?: Record<string, unknown>
  /** Server says this exact hash was already submitted; re-upload is pointless. */
  alreadyReceived?: { filename: string; status: string; verifiedAt: string }
}

function App() {
  const [file, setFile] = useState<File | null>(null)
  const [state, setState] = useState<VerificationState>('idle')
  const [result, setResult] = useState<VerificationResult | null>(null)
  const [password, setPassword] = useState('')
  const [passcodeSent, setPasscodeSent] = useState(false)
  const [sendingPasscode, setSendingPasscode] = useState(false)
  const [error, setError] = useState('')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const [fileHash, setFileHash] = useState<string | null>(null)
  /** Non-null when this exact hash is already on record at verifieddit.com. */
  const [alreadyVerified, setAlreadyVerified] = useState<{ shortUrl?: string } | null>(null)
  const [shortUrl, setShortUrl] = useState<string | null>(null)
  // Locally-rendered credit-card PNG returned by /api/drop/confirm-upload (Issue #56).
  // Preferred over the Verifieddit Worker URL when present.
  const [cardBadgeDataUri, setCardBadgeDataUri] = useState<string | null>(null)
  const [aiAnalyzing, setAiAnalyzing] = useState(false)
  const [aiResult, setAiResult] = useState<AiDetectApiResponse | null>(null)
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lon: number } | null>(null)
  const [uploaderEmail, setUploaderEmail] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [newsletterConsent, _setNewsletterConsent] = useState(false)
  const [isExistingUser, setIsExistingUser] = useState<boolean | null>(null)
  const [rejectionDetails, setRejectionDetails] = useState<{
    reason: string
    confidence: string
    generator: string
    badgeUrl: string
    proofUrl: string
  } | null>(null)
  const [rejectionEmail, setRejectionEmail] = useState('')
  const [rejectionEmailSent, setRejectionEmailSent] = useState(false)
  const [identityHydrated, setIdentityHydrated] = useState(false)
  const [identityName, setIdentityName] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Hydrate identity from URL query params set by /contact.html on the www site.
  // If no valid email is present, this page is being visited directly — redirect
  // back to /contact so the lead is captured first.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const emailParam = (params.get('email') || '').trim()
    const firstNameParam = (params.get('firstname') || '').trim()
    const lastNameParam = (params.get('lastname') || '').trim()
    const userStatusParam = (params.get('user_status') || '').trim()

    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailParam)) {
      setUploaderEmail(emailParam)
      setIdentityName(`${firstNameParam} ${lastNameParam}`.trim())
      if (userStatusParam === 'user') setIsExistingUser(true)
      else if (userStatusParam === 'prospect') setIsExistingUser(false)
      setIdentityHydrated(true)
      return
    }

    // No valid identity — bounce back to contact. Use a same-origin path
    // unless we're explicitly on production drop.thephenom.app (where contact
    // lives on a different host). Anything else (preview deployments,
    // localhost, NAS) is assumed to be co-hosting /contact.html.
    const target = window.location.hostname === 'drop.thephenom.app'
      ? 'https://www.thephenom.app/contact.html'
      : '/contact.html'
    window.location.replace(target)
  }, [])

  const handleFileSelect = useCallback((selectedFile: File) => {
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`File too large (${formatFileSize(selectedFile.size)}). Maximum size is ${formatFileSize(MAX_FILE_SIZE)}.`)
      return
    }
    setFile(selectedFile)
    setResult(null)
    setError('')
    setState('idle')
    setUploadProgress(0)
    setFileHash(null)
    setShortUrl(null)
    setCardBadgeDataUri(null)
    setRejectionDetails(null)
    setRejectionEmail('')
    setRejectionEmailSent(false)
    setGpsCoords(null)
  }, [])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) handleFileSelect(dropped)
  }, [handleFileSelect])

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const onDragLeave = useCallback(() => setDragOver(false), [])

  const verify = useCallback(async () => {
    if (!file) return
    setState('verifying')
    setError('')

    try {
      const isImage = file.type.startsWith('image/')

      // Compute file hash first — needed by AI detection and registration
      const hash = await computeFileHash(file)
      setFileHash(hash)

      // Dedupe gate: if verifieddit.com already holds this exact hash we have
      // the bytes, so re-uploading them is pure waste. Surface the existing
      // verification instead and keep the upload disabled.
      const known = await checkExistingHash(hash)
      if (known.exists) {
        setAlreadyVerified({ shortUrl: known.shortUrl })
        if (known.shortUrl) setShortUrl(known.shortUrl)
      } else {
        setAlreadyVerified(null)
      }

      // Extract GPS metadata for images. exifr.gps() is a focused, fast call
      // that returns {latitude, longitude} or null. Skip WebP (no EXIF parser
      // in exifr v7), video (different metadata format), and unsupported types.
      if (isImage && file.type !== 'image/webp') {
        try {
          const exifr = (await import('exifr')).default
          const gps = await exifr.gps(file).catch(() => null)
          if (gps && typeof gps.latitude === 'number' && typeof gps.longitude === 'number') {
            setGpsCoords({ lat: gps.latitude, lon: gps.longitude })
          }
        } catch {
          // Best-effort: missing GPS is the common case, not an error.
        }
      }

      // --- Local-first C2PA read (all files, any size) ---
      // The manifest is read in-browser with the WASM SDK. The reader seeks to
      // the JUMBF box rather than buffering the asset, so this costs kilobytes
      // whether the file is 2MB or 8GB — and the bytes never leave the device.
      // Reading locally is what lets us refuse to upload AI-declared media at
      // all: the verdict exists before a single byte is transferred. Sending the
      // file to the validate API first would defeat the entire point.
      {
        const local = await extractC2paLocally(file)
        const localAi = local.aiDetection?.isAiGenerated === true

        if (localAi) {
          setRejectionDetails({
            reason: local.aiDetection?.note || 'AI-generated content declared in C2PA metadata',
            confidence: '',
            generator: local.aiDetection?.detectedTools.join(', ') || '',
            badgeUrl: '',
            proofUrl: '',
          })
          setResult({
            hasC2PA: true,
            isValid: local.isValid,
            isTrusted: local.isTrusted,
            hasTimestamp: local.hasTimestamp,
            issuer: local.issuer,
            signedAt: local.signedAt,
            claimGenerator: local.claimGenerator,
            badgeStatus: 'invalid',
            manifestStore: local.manifestStore,
            aiDetection: local.aiDetection,
          })
          setState('rejected')
          return
        }

        setResult({
          hasC2PA: local.hasC2PA,
          isValid: local.isValid,
          isTrusted: local.isTrusted,
          hasTimestamp: local.hasTimestamp,
          issuer: local.issuer,
          signedAt: local.signedAt,
          claimGenerator: local.claimGenerator,
          badgeStatus: local.isTrusted ? 'trusted' : local.isValid ? 'valid' : 'unsigned',
          formatNote: `Media verified by Verifieddit.com`,
          manifestStore: local.manifestStore,
          aiDetection: local.aiDetection,
          // Hard-binding (does the manifest match THESE bytes) is confirmed
          // server-side after upload; the signature and signer above are already
          // verified locally.
          pendingServerVerification: !local.hasC2PA,
        })
        setState('verified')

        // Register the verification with verifieddit.com so the file gets a
        // durable short URL / badge record. Only derived metadata is sent —
        // never the asset. Non-blocking: the UI works if this fails.
        const statusMap: Record<BadgeStatus, string> = {
          trusted: 'verified', valid: 'authentic', invalid: 'invalid', unsigned: 'unsigned',
        }
        const badge: BadgeStatus = local.isTrusted ? 'trusted' : local.isValid ? 'valid' : 'unsigned'
        // Thumbnails come from a canvas decode, which is only sane for images.
        const thumb = isImage ? await generateThumbnail(file).catch(() => null) : null
        try {
          const vr = await createVerification({
            fileHash: hash,
            filename: file.name,
            c2paStatus: statusMap[badge],
            c2paIssuer: local.issuer,
            c2paSignedAt: local.signedAt,
            claimGenerator: local.claimGenerator,
            fileType: file.type,
            fileSize: file.size,
            aiDetected: false,
            thumbnailUrl: thumb || undefined,
          })
          if (vr?.shortUrl) setShortUrl(vr.shortUrl)
        } catch (err) {
          console.warn('verifieddit.com unavailable:', err)
        }
        return
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
      setState('error')
    }
  }, [file])

  const upload = useCallback(async () => {
    if (!file || !password) return
    setState('uploading')
    setError('')
    setUploadProgress(0)

    try {
      const response = await fetch(UPLOAD_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          fileHash: fileHash || '',
          email: uploaderEmail,
          password,
          newsletterConsent,
        }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Upload request failed (${response.status})`)
      }

      const data = await response.json()

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100))
          }
        })
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve()
          else reject(new Error(`Upload failed (${xhr.status})`))
        })
        xhr.addEventListener('error', () => reject(new Error('Upload failed')))

        const formData = new FormData()
        // data.fields already contains a `key` entry (AWS SDK's createPresignedPost
        // always includes it) — appending data.key separately here duplicated the
        // field, and S3 rejects a POST with two `key` fields: 400 InvalidArgument
        // "Bucket POST must contain exactly one field named 'key'". Fields (incl.
        // key) must precede `file` in the form, which this order already satisfies.
        for (const [name, value] of Object.entries(data.fields)) {
          formData.append(name, value as string)
        }
        formData.append('file', file)

        // Do not set Content-Type — the browser must supply the multipart boundary.
        xhr.open('POST', data.uploadUrl)
        xhr.send(formData)
      })

      // Gate success on confirm-upload — downstream sync depends on this.
      // email is included so the backend can mint the badge and send the
      // post-upload confirmation email only after a successful PUT.
      // GPS coords (if extracted from EXIF) ride along so the badge QR can
      // encode them and the email can render a Google Earth link.
      let confirmData: {
        shortUrl?: string
        cardBadgeDataUri?: string
        s3Key?: string
        mediaUrl?: string
        mediaGetUrl?: string
        serverVerification?: {
          hasC2PA?: boolean
          status?: string
          issuer?: string
          signedAt?: string
          claimGenerator?: string
        }
      } = {}
      if (data.key && fileHash) {
        const confirmBody: Record<string, unknown> = {
          fileHash,
          key: data.key,
          email: uploaderEmail,
        }
        if (gpsCoords) {
          confirmBody.gpsLat = gpsCoords.lat
          confirmBody.gpsLon = gpsCoords.lon
        }
        const confirmRes = await fetch(`${API_BASE}/api/drop/confirm-upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(confirmBody),
        })

        if (!confirmRes.ok) {
          const confirmErr = await confirmRes.json().catch(() => ({}))
          throw new Error(confirmErr.error || 'Upload confirmation failed — please try again')
        }

        // Capture badge artifacts from the confirm response (Issues #53, #56, #71).
        confirmData = await confirmRes.json().catch(() => ({}))
        if (confirmData.shortUrl) setShortUrl(confirmData.shortUrl)
        if (confirmData.cardBadgeDataUri) setCardBadgeDataUri(confirmData.cardBadgeDataUri)
      }

      setState('uploaded')

      // Server-side C2PA verification for video/large files (#71).
      // Preferred path: confirm-upload already ran the verification
      // BEFORE minting the badge and returns it as serverVerification.
      // Fallback: call verify-server with the S3 key (the backend
      // presigns a GET itself — the old downloadUrl field never existed).
      if (result?.pendingServerVerification) {
        const applyServerResult = (serverResult: {
          hasC2PA?: boolean
          status?: string
          issuer?: string
          signedAt?: string
          claimGenerator?: string
        }) => {
          if (serverResult.hasC2PA) {
            const status = serverResult.status || 'valid'
            setResult(prev => prev ? {
              ...prev,
              hasC2PA: true,
              isValid: status !== 'invalid',
              isTrusted: status === 'trusted',
              issuer: serverResult.issuer,
              signedAt: serverResult.signedAt,
              claimGenerator: serverResult.claimGenerator,
              badgeStatus: (status === 'trusted' || status === 'valid' || status === 'invalid')
                ? status as BadgeStatus : 'valid',
              formatNote: 'Verified by thephenom.ai',
              pendingServerVerification: false,
            } : prev)
          } else {
            setResult(prev => prev ? {
              ...prev,
              formatNote: 'Media verified by Verifieddit.com',
              pendingServerVerification: false,
            } : prev)
          }
        }

        // Hub URL mode (Issue #75): the media now has a hosted URL — post
        // it to the hub (no byte re-upload). mediaGetUrl is the presigned,
        // fetchable form; raw mediaUrl is the fallback. Legacy backend
        // verification paths remain as fallbacks until the production hub
        // is live (blocked by Sanmarcsoft/verifieddit-www#424).
        const hostedUrl = confirmData.mediaGetUrl || confirmData.mediaUrl
        const hubVerified = hostedUrl ? await verifyUrlViaHub(hostedUrl) : null
        if (hubVerified) {
          applyServerResult(hubResponseToServerVerification(hubVerified))
        } else if (confirmData?.serverVerification) {
          applyServerResult(confirmData.serverVerification)
        } else if (confirmData?.s3Key) {
          try {
            const verifyRes = await fetch(`${API_BASE}/api/drop/verify-server`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                key: confirmData.s3Key,
                filename: file.name,
                fileHash: fileHash || undefined,
              }),
            })
            if (verifyRes.ok) {
              applyServerResult(await verifyRes.json())
            }
          } catch (verifyErr) {
            console.warn('[C2PA] Server-side verification failed:', verifyErr)
            setResult(prev => prev ? { ...prev, pendingServerVerification: false } : prev)
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
      setState('error')
    }
  }, [file, password, uploaderEmail, newsletterConsent, result, fileHash])

  const sendPasscode = useCallback(async () => {
    if (!uploaderEmail.includes('@')) return
    setSendingPasscode(true)
    try {
      const res = await fetch(`${API_BASE}/api/drop/send-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: uploaderEmail, fileHash: fileHash || '' }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.error || 'Failed to send passcode')
        return
      }
      setPasscodeSent(true)
    } catch {
      setError('Failed to send passcode. Please try again.')
    } finally {
      setSendingPasscode(false)
    }
  }, [uploaderEmail, fileHash])

  const reset = useCallback(() => {
    setFile(null)
    setResult(null)
    setState('idle')
    setError('')
    setPassword('')
    setPasscodeSent(false)
    setSendingPasscode(false)
    setUploadProgress(0)
    setFileHash(null)
    setShortUrl(null)
    setCardBadgeDataUri(null)
    setRejectionDetails(null)
    setRejectionEmail('')
    setRejectionEmailSent(false)
    setIsExistingUser(null)
    setAlreadyVerified(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const isAiBlocked = result?.aiDetection?.isAiGenerated === true || (aiResult?.aiDetected === true && (aiResult?.confidence ?? 0) >= 0.20)
  const isMovFormat = file?.type === 'video/quicktime' || file?.name?.toLowerCase().endsWith('.mov')
  const needsServerSideVerification = !!(result?.formatNote) // MOV or >1GB — server-side only
  // Allow upload if: (C2PA valid) OR (needs server-side verification — MOV or >1GB)
  // Never upload bytes we already hold, and never upload media whose own C2PA
  // metadata declares it AI-generated.
  const canUpload = result && !isAiBlocked && !alreadyVerified && password.length > 0 && uploaderEmail.includes('@') && isExistingUser !== null && passcodeSent

  // Server-side AI detection for all images
  const isImage = result && file?.type?.startsWith('image/')
  const showAiConsent = isImage && !hasAiDetectConsent() && !aiResult && !aiAnalyzing

  const [aiAttempted, setAiAttempted] = useState(false)

  async function runAiDetection() {
    if (!file || !fileHash) return
    setAiAnalyzing(true)
    setAiAttempted(true)
    try {
      const apiResult = await analyzeImageForAi(file, fileHash)
      setAiResult(apiResult)
    } finally {
      setAiAnalyzing(false)
    }
  }

  // Auto-run if consent was previously given (for cases not handled in verify())
  // aiAttempted prevents infinite retry on failure
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shouldAutoRunAi = !!(isImage && hasAiDetectConsent() && !aiResult && !aiAnalyzing && !aiAttempted && fileHash)
  useEffect(() => {
    if (shouldAutoRunAi) runAiDetection()
  }, [shouldAutoRunAi]) // eslint-disable-line react-hooks/exhaustive-deps

  // Chrome (nav + hero-bg + footer) lives in the static index.html shell.
  // The React app only renders the upload workflow content.
  return (
    <div className="flex flex-col">
      <div className="flex-1 px-10 md:px-20 py-10">
        <div className="max-w-6xl mx-auto flex gap-8">
          {/* Left column — main content */}
          <div className="flex-1 space-y-8">

          {/* Step 1: Select file */}
          <section className="animate-fadeIn">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-phenom-red text-white text-xs font-bold flex items-center justify-center">1</span>
              <h2 className="text-sm font-semibold text-phenom-gray uppercase tracking-wider">Select Media</h2>
            </div>
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-phenom p-10 text-center cursor-pointer transition-all ${
                dragOver
                  ? 'border-phenom-cyan bg-phenom-cyan/5'
                  : file
                    ? 'border-green-500/40 bg-green-900/10'
                    : 'border-phenom-gray/30 hover:border-phenom-cyan/50 bg-phenom-dark/30'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,video/*"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) handleFileSelect(f)
                }}
              />
              {file ? (
                <div className="space-y-1">
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-phenom-gray text-sm">{formatSize(file.size)} &middot; {file.type}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-phenom-gray">Drop your media file here or click to browse</p>
                  <p className="text-phenom-gray/50 text-xs">Supports images and video (max {formatFileSize(MAX_FILE_SIZE)})</p>
                </div>
              )}
            </div>
          </section>

          {/* Step 2: Verify */}
          {file && (
            <section className="animate-slideUp">
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center ${result ? 'bg-green-600' : 'bg-phenom-red'}`}>2</span>
                <h2 className="text-sm font-semibold text-phenom-gray uppercase tracking-wider">Verify File Details</h2>
              </div>

              {!result && state !== 'verifying' && (
                <button
                  onClick={verify}
                  className="w-full py-3 bg-phenom-red hover:bg-phenom-red-dark text-white font-semibold rounded-phenom transition-colors"
                >
                  Verify File Details
                </button>
              )}

              {state === 'verifying' && (
                <div className="bg-[#1a1a1a] rounded-xl px-4 py-2.5 flex items-center gap-3 animate-fadeIn">
                  <div className="w-7 h-7 rounded-full border-2 flex items-center justify-center" style={{ borderColor: 'rgba(165,227,232,0.19)' }}>
                    <svg className="w-4 h-4 animate-spin" style={{ color: '#a5e3e8' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10" />
                    </svg>
                  </div>
                  <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 600, color: '#a5e3e8' }}>thephenom.ai is processing</span>
                  <div className="w-20 h-[3px] rounded-sm overflow-hidden" style={{ background: '#151515' }}>
                    <div className="h-full rounded-sm" style={{ width: '60%', background: 'linear-gradient(90deg, #06b6d4, #8b5cf6, #06b6d4)', backgroundSize: '200% 100%', animation: 'progressSlide 2s ease-in-out infinite' }} />
                  </div>
                  <style>{`
                    @keyframes progressSlide { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                  `}</style>
                </div>
              )}

              {result && <VerificationCard result={result} />}
            </section>
          )}

          {/* Server-side AI Detection for images */}
          {isImage && (
            <AiDetectionBanner
              showConsent={!!showAiConsent}
              onConsent={runAiDetection}
              analyzing={aiAnalyzing}
              result={aiResult}
            />
          )}

          {/* AI Content Block Gate */}
          {result?.hasC2PA && result?.isValid && isAiBlocked && (
            <section className="animate-slideUp">
              <div className="bg-red-950/40 border-2 border-red-500 rounded-phenom p-8 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-red-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-red-400 text-lg font-bold">AI-Generated Content Blocked</h3>
                    <p className="text-red-300/80 text-sm mt-1">
                      This media was identified as AI-generated content. AI-generated files cannot be submitted to Phenom Drop for UAP evidence. Only authentic, device-captured media is accepted.
                    </p>
                  </div>
                </div>
                <div className="bg-red-900/30 rounded-lg px-4 py-3 space-y-2">
                  {result.aiDetection?.detectedTools && result.aiDetection.detectedTools.length > 0 && (
                    <div className="text-sm text-red-300">
                      <span className="font-semibold text-red-400">AI Source: </span>
                      {result.aiDetection.detectedTools.join(', ')}
                    </div>
                  )}
                  {result.aiDetection?.digitalSourceType && (
                    <div className="text-sm text-red-300">
                      <span className="font-semibold text-red-400">Source Type: </span>
                      {result.aiDetection.digitalSourceType}
                    </div>
                  )}
                  {!result.aiDetection?.detectedTools?.length && !result.aiDetection?.digitalSourceType && (
                    <div className="text-sm text-red-300">
                      <span className="font-semibold text-red-400">Detected: </span>
                      {result.aiDetection?.note}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Rejection Panel — FAIL verdict */}
          {state === 'rejected' && rejectionDetails && (
            <section className="animate-slideUp">
              <div className="bg-red-950/40 border-2 border-red-500 rounded-phenom p-8 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-red-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-red-400 text-lg font-bold">
                      {rejectionDetails.reason.includes('AI') ? 'AI Content Detected' : 'Verification Failed'}
                    </h3>
                    <p className="text-red-300/80 text-sm mt-1">{rejectionDetails.reason}</p>
                  </div>
                </div>

                {(rejectionDetails.reason.includes('AI') && (rejectionDetails.confidence || rejectionDetails.generator)) && (
                  <div className="bg-red-900/30 rounded-lg px-4 py-3 space-y-2">
                    {rejectionDetails.confidence && (
                      <p className="text-sm text-red-300">
                        <span className="font-semibold text-red-400">Confidence: </span>
                        {(parseFloat(rejectionDetails.confidence) * 100).toFixed(1)}%
                      </p>
                    )}
                    {rejectionDetails.generator && (
                      <p className="text-sm text-red-300">
                        <span className="font-semibold text-red-400">Detected: </span>
                        {rejectionDetails.generator}
                      </p>
                    )}
                  </div>
                )}

                <p className="text-phenom-gray/70 text-sm">
                  This file cannot be uploaded. Enter your email to receive a verification badge.
                </p>

                {!rejectionEmailSent ? (
                  <div className="space-y-3">
                    <input
                      type="email"
                      value={rejectionEmail}
                      onChange={(e) => setRejectionEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-phenom-surface border border-phenom-gray/20 rounded-lg px-4 py-3 text-white placeholder-phenom-gray/50 focus:outline-none focus:border-phenom-cyan/50"
                    />
                    <button
                      onClick={async () => {
                        if (!rejectionEmail.includes('@')) return
                        try {
                          await fetch(`${API_BASE}/api/drop/send-rejection`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              email: rejectionEmail,
                              fileHash,
                              rejectionReason: rejectionDetails.reason,
                              aiConfidence: rejectionDetails.confidence,
                              detectedGenerator: rejectionDetails.generator,
                              badgeUrl: rejectionDetails.badgeUrl,
                              proofUrl: rejectionDetails.proofUrl,
                            }),
                          })
                        } catch (err) {
                          console.warn('Failed to send rejection email:', err)
                        }
                        setRejectionEmailSent(true)
                      }}
                      disabled={!rejectionEmail.includes('@')}
                      className={`w-full py-3 font-semibold rounded-phenom transition-colors ${
                        rejectionEmail.includes('@')
                          ? 'bg-phenom-red hover:bg-phenom-red-dark text-white'
                          : 'bg-phenom-surface text-phenom-gray/50 cursor-not-allowed'
                      }`}
                    >
                      Send Badge
                    </button>
                  </div>
                ) : (
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-center">
                    <p className="text-green-400 font-medium">Verification badge sent to {rejectionEmail}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Server-Side Verification Notice (MOV format or file >1GB) */}
          {result && needsServerSideVerification && !result.hasC2PA && (
            <section className="animate-slideUp">
              <div className="bg-phenom-dark rounded-phenom p-6 border border-amber-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{isMovFormat ? '🎬' : '📦'}</span>
                  <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
                    {isMovFormat ? 'MOV Format Detected' : 'Large File Detected'}
                  </h3>
                </div>
                <p className="text-sm text-phenom-gray leading-relaxed">
                  {result.formatNote}
                </p>
                <p className="text-xs text-phenom-gray/60 mt-2">
                  You may proceed with the upload. Verification results will appear as a verification badge in the passcode email.
                </p>
              </div>
            </section>
          )}

          {/* Verification Proof — only after upload is confirmed AND a Verifieddit
              short URL has been minted (Issue #53). The badge image is a PNG
              fetched from Verifieddit (Issue #55 — no SVG badges).
              Pre-upload, no badge artifact exists, so this section stays hidden. */}
          {state === 'uploaded' && shortUrl && result && (() => {
            // Prefer the locally-rendered credit-card PNG from confirm-upload
            // (Issue #56), fall back to the Verifieddit Worker URL.
            const fallbackPngUrl = badgePngUrlFromShortUrl(shortUrl)
            const badgeImgSrc = cardBadgeDataUri || fallbackPngUrl
            if (!badgeImgSrc) return null
            return (
            <section className="animate-slideUp">
              <div className="flex items-center gap-2 mb-3">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a5e3e8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
                <h2 className="text-sm font-semibold text-phenom-gray uppercase tracking-wider">Verification Proof</h2>
              </div>
              <div className="bg-phenom-dark rounded-phenom p-6 space-y-4">
                <div className="flex items-center gap-4">
                  <img
                    src={badgeImgSrc}
                    alt="Verification badge"
                    className="h-16 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-phenom-gray text-sm">
                      Your verified media has been registered with{' '}
                      <a href="https://verifieddit.com" target="_blank" rel="noopener noreferrer" className="text-phenom-cyan hover:underline">verifieddit.com</a>
                    </p>
                    <p className="text-phenom-cyan/70 text-xs mt-1 truncate">
                      Proof: <a href={shortUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">{shortUrl}</a>
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <a
                    href={badgeImgSrc}
                    download={`${file?.name?.replace(/\.[^.]+$/, '') || 'media'}-verifieddit-badge.png`}
                    className="flex-1 py-2.5 bg-phenom-surface hover:bg-phenom-gray/20 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Badge
                  </a>
                  <button
                    onClick={() => {
                      if (!file) return
                      exportVerificationCertificate({
                        name: file.name,
                        type: file.type,
                        size: file.size,
                        hasC2PA: true,
                        fileHash: fileHash || undefined,
                        shortUrl: shortUrl || undefined,
                        aiDetected: result.aiDetection,
                        c2paDetails: {
                          hasValidSignature: result.isValid,
                          hasTrustedSigner: result.isTrusted,
                          hasErrors: result.badgeStatus === 'invalid',
                          hasWarnings: !result.isTrusted && result.isValid,
                          activeManifest: {
                            claimGenerator: result.claimGenerator,
                            signatureInfo: result.issuer ? { issuer: result.issuer, time: result.signedAt } : null,
                          },
                        },
                      })
                    }}
                    className="flex-1 py-2.5 bg-phenom-surface hover:bg-phenom-gray/20 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                    Certificate
                  </button>
                </div>
              </div>
            </section>
          )
          })()}

          {/* Step 3: Upload (only if verified + not AI blocked) */}
          {result && !isAiBlocked && state !== 'rejected' && (
            <section className="animate-slideUp">
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${result?.isValid || needsServerSideVerification ? 'bg-[#d73429] text-white' : 'bg-[#151515] text-[#aab1be80]'}`}>2</span>
                <h2 className={`text-sm font-semibold uppercase tracking-wider ${result?.isValid || needsServerSideVerification ? 'text-phenom-gray' : 'text-phenom-gray/50'}`}>Upload</h2>
              </div>

              <div className="bg-phenom-dark rounded-phenom p-6 space-y-4">
                {/* Already-on-record notice — we hold these bytes, so uploading
                    them again is pointless. Upload stays disabled. */}
                {alreadyVerified && (
                  <div className="flex gap-2.5 rounded-lg p-3" style={{ background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.28)' }}>
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg>
                    <div>
                      <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', lineHeight: 1.5, color: 'rgba(234,179,8,0.95)', margin: 0 }}>
                        We already have this file — it was verified previously, so there is no need to upload it again.
                      </p>
                      {alreadyVerified.shortUrl && (
                        <a href={alreadyVerified.shortUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', color: '#60A5FA', textDecoration: 'underline' }}>
                          View its existing verification
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Email info note */}
                <div className="flex gap-2.5 rounded-lg p-3" style={{ background: 'rgba(165,227,232,0.05)', border: '1px solid rgba(165,227,232,0.13)' }}>
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="#a5e3e8" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', lineHeight: 1.5, color: 'rgba(165,227,232,0.8)' }}>
                    Your email is required to receive your secure upload passcode and helps us minimize abuse. We may also send occasional Phenom news — you can unsubscribe anytime.
                  </p>
                </div>

                {/* User type radio — skipped when /contact already told us the answer.
                    A hydrated identity that carried no (or an unrecognised) user_status
                    still needs to be asked, otherwise isExistingUser stays null and the
                    upload button can never enable. */}
                {(!identityHydrated || isExistingUser === null) && (
                  <div className="space-y-3">
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '13px', fontWeight: 500, color: '#fff' }}>
                      Are you already a user of The Phenom App?
                    </p>
                    <div className="flex gap-6">
                      {['Yes', 'No'].map((option) => (
                        <label key={option} className="flex items-center gap-2 cursor-pointer" onClick={() => setIsExistingUser(option === 'Yes')}>
                          <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center ${
                            isExistingUser === (option === 'Yes') ? 'border-[#a5e3e8]' : 'border-[#aab1be40]'
                          }`}>
                            {isExistingUser === (option === 'Yes') && <div className="w-2 h-2 rounded-full bg-[#a5e3e8]" />}
                          </div>
                          <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '13px', fontWeight: 500, color: isExistingUser === (option === 'Yes') ? '#fff' : '#aab1be' }}>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Identity confirmation block — shown when arriving from /contact */}
                {identityHydrated && (
                  <div className="rounded-lg p-3 border" style={{ background: '#151515', borderColor: 'rgba(165,227,232,0.3)' }}>
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '10px', color: 'rgba(170,177,190,0.6)', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Continuing as</p>
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '14px', fontWeight: 600, color: '#fff', margin: '4px 0 2px' }}>{identityName || uploaderEmail}</p>
                    <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', color: '#aab1be', margin: 0 }}>{uploaderEmail} · {isExistingUser ? 'existing user' : 'new to Phenom'}</p>
                  </div>
                )}

                {/* Email field + Send Passcode button.
                    - Hydrated path (visit from /contact): no email input,
                      identity already shown in 'Continuing as' card. Just
                      a full-width 'Send passcode to ma•••@example.com'.
                    - Direct path (fallback only — bare visits get bounced
                      to /contact by the guard): full email entry UI. */}
                {(identityHydrated || isExistingUser !== null) && (
                  <div className="space-y-2 animate-fadeIn">
                    {!identityHydrated && (
                      <>
                        <label style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 500, color: '#aab1be' }}>
                          {isExistingUser ? 'Phenom App email address' : 'Email address'}
                        </label>
                        {isExistingUser && (
                          <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '10px', color: 'rgba(170,177,190,0.38)' }}>
                            Enter the email you registered with in the app
                          </p>
                        )}
                      </>
                    )}
                    <div className="flex gap-2">
                      {!identityHydrated && (
                        <input
                          type="email"
                          placeholder="you@example.com"
                          value={uploaderEmail}
                          onChange={(e) => { setUploaderEmail(e.target.value); setPasscodeSent(false) }}
                          className="flex-1 bg-[#151515] border rounded-lg px-4 py-3 text-white placeholder-phenom-gray/50 focus:outline-none"
                          style={{ borderColor: 'rgba(170,177,190,0.19)', fontFamily: "'Roboto Mono', monospace", fontSize: '14px' }}
                          disabled={state === 'uploading' || state === 'uploaded'}
                        />
                      )}
                      <button
                        onClick={sendPasscode}
                        disabled={!uploaderEmail.includes('@') || sendingPasscode || state === 'uploading' || state === 'uploaded'}
                        className={`${identityHydrated ? 'flex-1 ' : ''}px-4 py-3 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                          uploaderEmail.includes('@') && !sendingPasscode
                            ? passcodeSent
                              ? 'bg-green-800/30 border border-green-500/30 text-green-400'
                              : 'bg-phenom-red hover:bg-phenom-red-dark text-white'
                            : 'bg-phenom-surface text-phenom-gray/50 cursor-not-allowed'
                        }`}
                      >
                        {sendingPasscode
                          ? 'Sending…'
                          : passcodeSent
                            ? `Sent to ${uploaderEmail.replace(/^(.{2}).*(@.+)$/, '$1•••$2')}`
                            : `Send passcode${identityHydrated ? ' to ' + uploaderEmail.replace(/^(.{2}).*(@.+)$/, '$1•••$2') : ''}`}
                      </button>
                    </div>
                    {passcodeSent && (
                      <p style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', color: 'rgba(74,222,128,0.8)' }}>
                        Passcode sent — check your inbox
                      </p>
                    )}
                  </div>
                )}

                {/* Passcode field — shown after code is sent */}
                {passcodeSent && (
                  <div className="relative animate-fadeIn">
                    <input
                      type="password"
                      placeholder="Enter passcode"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-phenom-surface border border-phenom-gray/20 rounded-lg px-4 py-3 text-white placeholder-phenom-gray/50 focus:outline-none focus:border-phenom-cyan/50"
                      disabled={state === 'uploading' || state === 'uploaded'}
                    />
                  </div>
                )}

                {state === 'uploading' && (
                  <div className="space-y-2">
                    <div className="w-full bg-phenom-surface rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-phenom-cyan h-full rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-phenom-gray text-sm text-center">{uploadProgress}% uploaded</p>
                  </div>
                )}

                {state === 'uploaded' && (
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-center">
                    <p className="text-green-400 font-medium">Upload complete! Your verified media has been submitted.</p>
                  </div>
                )}

                {state !== 'uploading' && state !== 'uploaded' && (
                  <button
                    onClick={upload}
                    disabled={!canUpload}
                    className={`w-full py-3 font-semibold rounded-phenom transition-colors ${
                      canUpload
                        ? 'bg-phenom-red hover:bg-phenom-red-dark text-white'
                        : 'bg-phenom-surface text-phenom-gray/50 cursor-not-allowed'
                    }`}
                  >
                    Upload Verified Media
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Info message for unsigned files */}
          {result && !result.hasC2PA && state !== 'rejected' && (
            <section className="animate-slideUp">
              <div className="bg-amber-900/10 border border-amber-500/20 rounded-phenom p-4 text-center space-y-1">
                <p className="text-amber-400/80 text-sm font-medium">No provenance detected</p>
                <p className="text-phenom-gray/60 text-xs">This file has no provenance. It can still be uploaded.</p>
              </div>
            </section>
          )}

          {/* Error display */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-phenom p-4 text-center space-y-3">
              <p className="text-red-400 text-sm">{error}</p>
              {result && state === 'error' && (
                <button
                  onClick={() => { setState('verified'); setError(''); }}
                  className="px-6 py-2 bg-phenom-red hover:bg-phenom-red-dark text-white text-sm font-semibold rounded-phenom transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          )}

          {/* Reset */}
          {(result || error) && (
            <button
              onClick={reset}
              className="text-phenom-gray hover:text-white text-sm underline underline-offset-4 transition-colors"
            >
              Start over with a different file
            </button>
          )}
          </div>

          {/* Right sidebar — only shows after file selected */}
          {file && (
            <div className="w-[300px] flex-shrink-0 space-y-4" style={{ paddingTop: '44px' }}>
              {/* FILE DETAILS card */}
              <div className="bg-[#1a1a1a] rounded-xl p-5 space-y-3">
                <h3 className="text-[11px] font-semibold tracking-[2px] uppercase" style={{ color: '#aab1be', fontFamily: "'Roboto Mono', monospace" }}>File Details</h3>
                <div className="h-px" style={{ background: 'rgba(170,177,190,0.13)' }} />
                {[
                  ['Filename', file.name],
                  ['Size', formatFileSize(file.size)],
                  ['Type', file.type],
                  ['SHA-256', fileHash ? `${fileHash.substring(0,4)}...${fileHash.substring(fileHash.length-4)}` : '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between">
                    <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: 'rgba(170,177,190,0.38)' }}>{label}</span>
                    <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 500, color: label === 'SHA-256' ? '#a5e3e8' : '#fff' }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* VERIFICATION STATUS card — only after verification.
                  When the file has no C2PA metadata, signature/trust/timestamp
                  checks are not applicable — show a neutral "No provenance"
                  state and suppress the irrelevant pills entirely. */}
              {result && (
                <div className="bg-[#1a1a1a] rounded-xl p-5 space-y-3">
                  <h3 className="text-[11px] font-semibold tracking-[2px] uppercase" style={{ color: '#aab1be', fontFamily: "'Roboto Mono', monospace" }}>Verification Status</h3>
                  <div className="h-px" style={{ background: 'rgba(170,177,190,0.13)' }} />
                  {!result.hasC2PA ? (
                    <div className="flex items-center gap-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-500" />
                      <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 600, color: '#aab1be' }}>
                        No provenance metadata found
                      </span>
                    </div>
                  ) : result.pendingServerVerification ? (
                    /* The ranged box scan found a manifest but nothing has been
                       validated yet — signature and trust checks need the media
                       bytes, which only the server sees after upload. Saying
                       "Invalid Signature" here would be a false accusation. */
                    <div className="flex items-center gap-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#a5e3e8]" />
                      <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 600, color: '#a5e3e8' }}>
                        Provenance found — verification completes after upload
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${result.isTrusted ? 'bg-green-500' : result.isValid ? 'bg-yellow-500' : 'bg-red-500'}`} />
                        <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 600, color: result.isTrusted ? '#22c55e' : result.isValid ? '#eab308' : '#ef4444' }}>
                          {result.isTrusted ? 'Verified — Trusted Signer' : result.isValid ? 'Valid — Untrusted Signer' : 'Invalid Signature'}
                        </span>
                      </div>
                      <div className="flex gap-1.5">
                        {[
                          ['Signature', result.isValid],
                          ['Trusted', result.isTrusted],
                          ['Timestamp', result.hasTimestamp],
                        ].map(([label, ok]) => (
                          <div key={label as string} className="flex-1 rounded-lg py-2 text-center" style={{ background: ok ? 'rgba(20,83,45,0.1)' : '#151515' }}>
                            <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '11px', color: ok ? '#4ade80' : 'rgba(170,177,190,0.3)' }}>
                              {ok ? '✓' : '—'} {label as string}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* CAPTURE LOCATION map — shown when EXIF GPS data is found.
                  Independent of provenance state: a file can have GPS but no
                  C2PA, and vice versa. */}
              {gpsCoords && (
                <GPSMap lat={gpsCoords.lat} lon={gpsCoords.lon} fileName={file?.name} />
              )}

              {/* PROVENANCE card — only if C2PA data */}
              {result?.hasC2PA && (
                <div className="bg-[#1a1a1a] rounded-xl p-5 space-y-3">
                  <h3 className="text-[11px] font-semibold tracking-[2px] uppercase" style={{ color: '#aab1be', fontFamily: "'Roboto Mono', monospace" }}>Provenance</h3>
                  <div className="h-px" style={{ background: 'rgba(170,177,190,0.13)' }} />
                  {[
                    ['Issuer', result.issuer || '—'],
                    ['Signed', result.signedAt || '—'],
                    ['Generator', result.claimGenerator || '—'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: 'rgba(170,177,190,0.38)' }}>{label}</span>
                      <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: '12px', fontWeight: 500, color: '#fff' }}>{value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Provenance chain diagram — same Mermaid rendering verifieddit.com
                  and trusteddit.com use. Driven by the manifest store we read
                  locally, so it appears before anything is uploaded. */}
              {/* The generator yields nothing for a single-manifest file with no
                  ingredients — a chain of one is not a chain. Don't render an
                  empty titled card in that case. */}
              {result?.manifestStore && generateMermaidDiagram(result.manifestStore as never) !== '' && (
                <div className="bg-[#1a1a1a] rounded-xl p-5 space-y-3">
                  <h3 className="text-[11px] font-semibold tracking-[2px] uppercase" style={{ color: '#aab1be', fontFamily: "'Roboto Mono', monospace" }}>Provenance Chain</h3>
                  <div className="h-px" style={{ background: 'rgba(170,177,190,0.13)' }} />
                  <ProvenanceDiagram manifestStore={result.manifestStore} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Build stamp — near-invisible (matches bg), read via devtools/console, not for display */}
      <div
        aria-hidden="true"
        style={{ position: 'fixed', bottom: 2, right: 2, fontSize: '8px', lineHeight: 1, color: '#0a0a0a', userSelect: 'none', pointerEvents: 'none' }}
      >
        {__GIT_SHA__}&middot;{__BUILD_TIME__}
      </div>
    </div>
  )
}

function VerificationCard({ result }: { result: VerificationResult }) {
  // No badge image rendered here — pre-upload there is no durable badge artifact
  // (Issue #53), and badges must be PNG-only post-upload (Issue #55). The card's
  // border colour + statusLabel carry the visual hierarchy.
  const ecosystem = isEcosystemSigner(result.issuer ?? null)

  const statusColor = result.badgeStatus === 'trusted'
    ? 'border-green-500/40 bg-green-900/10'
    : result.badgeStatus === 'valid'
      ? 'border-amber-500/40 bg-amber-900/10'
      : result.badgeStatus === 'invalid'
        ? 'border-red-500/40 bg-red-900/10'
        : 'border-phenom-gray/30 bg-phenom-dark/30'

  // Ecosystem signers get branded language (no C2PA terminology), but the
  // label must still reflect badgeStatus — the actual cryptographic trust
  // result — not just issuer string-matching. Otherwise a valid-but-untrusted
  // signer (e.g. a real CA the trust list hasn't been updated for) shows as
  // "Verified Authentic" while the detail panel says "Untrusted Signer".
  // badgeStatus stays 'unsigned' until the server validates, so a file whose
  // manifest we found by ranged scan must not be labelled as having none.
  const statusLabel = result.badgeStatus === 'trusted'
    ? (ecosystem ? 'Verified Authentic' : 'Verified')
    : result.badgeStatus === 'valid'
      ? (ecosystem ? 'Authentic — Pending Trust' : 'Valid — Untrusted Signer')
      : result.hasC2PA && result.pendingServerVerification ? 'Provenance found — pending verification'
      : result.badgeStatus === 'unsigned' ? 'No provenance metadata found'
      : result.badgeStatus

  return (
    <div className={`border rounded-phenom p-6 space-y-4 ${statusColor}`}>
      <div className="flex items-start gap-4">
        <div className="space-y-1 flex-1 min-w-0">
          <p className="text-white font-semibold capitalize">{statusLabel}</p>
          {result.issuer && (
            <p className="text-phenom-gray text-sm truncate">
              {ecosystem ? 'Verified by' : 'Signed by'}: {result.issuer}
            </p>
          )}
          {result.signedAt && (
            <p className="text-phenom-gray text-sm">
              {ecosystem ? 'Captured' : 'Signed'}: {new Date(result.signedAt).toLocaleString()}
            </p>
          )}
          {result.claimGenerator && !ecosystem && (
            <p className="text-phenom-gray text-sm truncate">Tool: {result.claimGenerator}</p>
          )}
        </div>
      </div>

      {/* Recovery note */}
      {result.formatNote && result.hasC2PA && (
        <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg px-4 py-3">
          <p className="text-amber-300/80 text-xs leading-relaxed">{result.formatNote}</p>
        </div>
      )}

      {/* Status details — ecosystem signers get user-friendly labels.
          Hidden when no provenance is present: signature/trust/timestamp
          checks are not applicable to an unsigned file. */}
      {result.hasC2PA && (
        <div className="grid grid-cols-3 gap-3 text-xs">
          <StatusPill label={ecosystem ? 'Authentic' : 'Signature'} ok={result.isValid} />
          <StatusPill label={ecosystem ? 'Verified' : 'Trusted'} ok={result.isTrusted} />
          <StatusPill label="Timestamp" ok={result.hasTimestamp} />
        </div>
      )}

      {/* AI detection warning — only when the manifest actually declares AI.
          aiDetection is now populated for every signed file (including clean
          ones), so this must key off the verdict, not mere presence. */}
      {result.aiDetection?.isAiGenerated === true && (
        <div className="bg-red-900/30 border border-red-500/40 rounded-lg px-4 py-3 flex items-center gap-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
            <circle cx="12" cy="12" r="10"/>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
          </svg>
          <div>
            <p className="text-red-400 text-xs font-semibold">AI Content - Upload Blocked</p>
            <p className="text-red-300/70 text-xs">
              {result.aiDetection.detectedTools.length > 0
                ? `Source: ${result.aiDetection.detectedTools.join(', ')}`
                : result.aiDetection.note}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

function StatusPill({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className={`rounded-lg px-2 py-1.5 text-center ${
      ok ? 'bg-green-900/30 text-green-400' : 'bg-phenom-surface text-phenom-gray/50'
    }`}>
      <span className="mr-1">{ok ? '✓' : '—'}</span>
      {label}
    </div>
  )
}

export default App
